export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) {
    node.textContent = text;
  }
  return node;
}

export function button(className: string, text: string): HTMLButtonElement {
  const node = el('button', className, text);
  node.type = 'button';
  node.tabIndex = -1;
  return node;
}

// A click a keyboard or a pad makes (no pointer) already went through the cursor.
export function onPointerClick(node: HTMLElement, action: () => void): void {
  node.addEventListener('click', (event) => {
    if (event.detail !== 0) {
      action();
    }
  });
}

export function findAll<T extends Element>(
  root: ParentNode,
  selector: string,
  type: abstract new () => T,
): T[] {
  return [...root.querySelectorAll(selector)].filter((node): node is T => node instanceof type);
}

export function icon(className: string, svgBody: string, viewBox = '0 0 24 24'): HTMLSpanElement {
  const node = el('span', className);
  node.setAttribute('aria-hidden', 'true');
  node.innerHTML = `<svg viewBox="${viewBox}" focusable="false">${svgBody}</svg>`;
  return node;
}

export interface GoMark {
  readonly element: HTMLElement;
  set(label: string): void;
}

export function goMark(svgBody: string): GoMark {
  const element = el('span', 'ui-go');
  element.setAttribute('aria-hidden', 'true');
  const label = el('span', 'ui-go__label');
  element.append(icon('ui-go__icon', svgBody), label);
  return {
    element,
    set(text) {
      setText(label, text);
    },
  };
}

const ARROW_TURNS: Readonly<Record<string, number>> = { '↑': 0, '→': 90, '↓': 180, '←': 270 };

// Arrow glyphs sit at a different height in each font: they are drawn, and keep their text for screen readers.
export function keycap(label: string, style: 'key' | 'button'): HTMLElement {
  const turn = ARROW_TURNS[label];
  if (turn === undefined) {
    return el('kbd', `ui-key ui-key--${style}`, label);
  }
  const key = el('kbd', `ui-key ui-key--${style} ui-key--arrow`);
  key.append(
    icon(
      'ui-key__arrow',
      `<path d="M12 19V5M6 11l6-6 6 6" transform="rotate(${String(turn)} 12 12)" fill="none" ` +
        'stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/>',
    ),
    el('span', 'ui-key__text', label),
  );
  return key;
}

export interface HintPart {
  keys: readonly string[];
  label: string;
  // For a menu whose layout changes between wide and narrow screens.
  screen?: 'wide' | 'narrow';
}

export function fillHint(
  container: HTMLElement,
  parts: readonly HintPart[],
  style: 'key' | 'button',
): void {
  container.replaceChildren(
    ...parts.map((part) => {
      const item = el(
        'span',
        part.screen === undefined ? 'ui-hint__part' : `ui-hint__part ui-hint__part--${part.screen}`,
      );
      const keys = el('span', 'ui-hint__keys');
      keys.append(...part.keys.map((key) => keycap(key, style)));
      item.append(keys, el('span', 'ui-hint__label', part.label));
      return item;
    }),
  );
}

export function setText(node: HTMLElement, text: string): void {
  if (node.textContent !== text) {
    node.textContent = text;
  }
}

export function setVar(node: HTMLElement, name: string, value: string): void {
  if (node.style.getPropertyValue(name) !== value) {
    node.style.setProperty(name, value);
  }
}

export function setFlag(node: Element, name: string, on: boolean): void {
  if (on) {
    node.setAttribute(`data-${name}`, '');
  } else {
    node.removeAttribute(`data-${name}`);
  }
}

export async function copyText(
  clipboard: Pick<Clipboard, 'writeText'> | undefined,
  text: string,
): Promise<boolean> {
  if (clipboard === undefined) {
    return false;
  }
  try {
    await clipboard.writeText(text);
    return true;
  } catch (error) {
    if (error instanceof DOMException) {
      return false;
    }
    throw error;
  }
}
